import {observer} from 'mobx-react';
import {useRouter} from 'next/router';
import React from 'react';

import {TrackerInitializer} from '@udemy/event-tracking';
import {Footer} from '@udemy/footer';
import {Header} from '@udemy/react-header';
import {Toaster} from '@udemy/react-messaging-components';
import {useStores} from '@udemy/store-provider';

import {RootStore} from 'src/stores/root-store';

import {useShouldShowSmartBar} from '../without-smart-bar/use-should-show-smart-bar';
import styles from './layout.module.less';

/**
 * Page shell rendered by `getLayout`.
 *
 * This file is `file.Once`, so stencil never overwrites it. It replaces the default `Layout`
 * from `layout.tsx` to keep the app-specific behavior below: hiding Header/Footer for the
 * `display_type=mobile_app` webview and hiding the Smart Bar (experiment or `hideSmartBar`).
 */
export const AppLayout = observer(
    ({
        children,
        showHeader = true,
        showFooter = true,
        hideSmartBar = false,
    }: {
        children: React.ReactElement;
        showHeader?: boolean;
        showFooter?: boolean;
        hideSmartBar?: boolean;
    }) => {
        const [rootStore] = useStores([RootStore]);
        const router = useRouter();
        const hasMobileAppQueryParam = (router.query.display_type as string) === 'mobile_app';
        const showSmartBar = useShouldShowSmartBar(rootStore.udData, hideSmartBar);

        return (
            <>
                {/* TODO: remove SmartBar rendering from Header component, re-add it here */}
                {/* <SmartBar /> */}
                <div className={styles['container']}>
                    {showHeader && !hasMobileAppQueryParam && (
                        <Header {...rootStore.headerData} showSmartBar={showSmartBar} />
                    )}
                    <main className={styles['content']} id="main-content-anchor">
                        {children}
                    </main>
                    {showFooter && !hasMobileAppQueryParam && (
                        <Footer
                            clientProps={rootStore.footerData}
                            serverProps={rootStore.footerData}
                        />
                    )}
                    <Toaster />
                </div>
                <TrackerInitializer />
            </>
        );
    },
);
